<template>
  <div>
    <div class="container">
      <div>
        <template>
          <div class="row">
            <v-text-field
              v-model="startDate"
              :disabled="disabled || disabledStart"
              v-on:click="
                e => {
                  e.stopPropagation()
                  if (!isEditableStartDate) return

                  changeIsOpenCalendar('start')
                }
              "
              label="開始日"
              prepend-icon="event"
              readonly
              class="text"
            />
            <v-btn
              v-if="!disabledStart"
              v-on:click="() => (startDate = undefined)"
              :disabled="
                startDate === null || (startDate === null && endDate === null)
              "
              variant="plain"
              icon="icon"
              class="button-delete"
            >
              <v-icon>mdi-close-circle-outline</v-icon>
            </v-btn>
          </div>
        </template>
      </div>
      <div class="dash pa-2">-</div>
      <div>
        <template>
          <div class="row">
            <v-text-field
              v-model="endDate"
              :clearable="clearable"
              :disabled="startDate === null"
              v-on:click="
                e => {
                  e.stopPropagation()
                  changeIsOpenCalendar('end')
                }
              "
              label="終了日"
              prepend-icon="event"
              readonly
            />
            <v-btn
              v-on:click="() => (endDate = undefined)"
              :disabled="
                endDate === null || (startDate === null && endDate === null)
              "
              variant="plain"
              icon="icon"
              class="button-delete"
            >
              <v-icon>mdi-close-circle-outline</v-icon>
            </v-btn>
          </div>
        </template>
      </div>
    </div>
    <div
      v-on:click="e => e.stopPropagation()"
      class="date-picker-container"
      style="width: 290px"
    >
      <div v-if="isOpenStartCalendar">
        <v-date-picker
          v-model="calendarStartDate"
          v-on:click="e => e.preventDefault()"
          :no-title="true"
          :allowed-dates="allowedDatesOfStart"
          :day-format="formatDate"
          :events="isOccupied"
          locale="ja"
          class="date-picker"
        />
      </div>
    </div>
    <div
      v-on:click="e => e.stopPropagation()"
      class="date-picker-container"
      style="width: 0px; margin-left: 250px;"
    >
      <div v-if="!isOpenStartCalendar">
        <v-date-picker
          v-if="isOpenEndCalendar"
          v-model="calendarEndDate"
          v-on:click="e => e.preventDefault()"
          :no-title="true"
          :allowed-dates="allowedDatesOfEnd"
          :day-format="formatDate"
          :events="isOccupied"
          locale="ja"
          class="date-picker"
        />
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)

const dateFormat = 'YYYY-MM-DD'

export default {
  name: 'BudgetPeriodPicker',
  props: {
    start: {
      type: String,
      default: '',
    },
    end: {
      type: String,
      default: '',
    },
    clearable: { type: Boolean, default: false },
    disabled: Boolean,
    usableStartDateFilter: {
      type: Function,
      required: true,
      default: () => {},
    },
    usableEndDateFilter: {
      type: Function,
      required: true,
      default: () => {},
    },
    isDateRestrictions: {
      type: Boolean,
      default: true,
    },
    occupiedPeriods: {
      type: Array,
      default: () => [],
    },
    isOpenStartCalendar: {
      type: Boolean,
      default: false,
    },
    isOpenEndCalendar: {
      type: Boolean,
      default: false,
    },
    changeIsOpenCalendar: {
      type: Function,
      default: () => {},
    },
    dummyStartDateForInitialMonth: {
      type: String,
      default: '',
    },
    dummyEndDateForInitialMonth: {
      type: String,
      default: '',
    },
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      initialStart: this.start,
      initialEnd: this.end,
      errors: { start: '', end: '' },
    }
  },
  computed: {
    isEditableStartDate() {
      return this.isNew || !this.start || dayjs(this.start).isAfter(dayjs())
    },
    startDate: {
      get() {
        if (!this.start) return null
        return dayjs(this.start).format(dateFormat)
      },
      set(value) {
        this.changeIsOpenCalendar('start')

        if (value === undefined) {
          this.$emit('inputStartDate', undefined)
          return
        }

        this.$emit('inputStartDate', this.format(value))
      },
    },
    calendarStartDate: {
      // eslint-disable-next-line vue/return-in-computed-property
      get() {
        if (this.start) return dayjs(this.start).format(dateFormat)
        if (this.end) return dayjs(this.end).format(dateFormat)
        if (this.dummyStartDateForInitialMonth)
          return dayjs(this.dummyStartDateForInitialMonth).format(dateFormat)
        if (!this.start) return null
      },
      set(value) {
        this.changeIsOpenCalendar('start')
        this.$emit('inputStartDate', this.format(value))
      },
    },
    endDate: {
      get() {
        if (!this.end) return null
        return dayjs(this.end).format(dateFormat)
      },
      set(value) {
        this.changeIsOpenCalendar('end')

        if (value === undefined) {
          this.$emit('inputEndDate', undefined)
          return
        }

        this.$emit('inputEndDate', this.format(value))
      },
    },
    calendarEndDate: {
      get() {
        if (this.dummyEndDateForInitialMonth)
          return this.dummyEndDateForInitialMonth

        if (!this.end) return null

        return dayjs(this.end).format(dateFormat)
      },
      set(value) {
        console.log('[1] calendarEndDate.set value:', value)
        this.changeIsOpenCalendar('end')
        const formatted = this.format(value)
        console.log('[2] formatted endDate:', formatted)
        this.$emit('inputEndDate', this.format(value))
      },
    },
    /**
     * すでに開始しているかどうか
     */
    alreadyStarted() {
      return this.initialStart && dayjs().isAfter(this.initialStart)
    },
    disabledStart() {
      return !this.isNew && this.alreadyStarted
    },
  },
  methods: {
    allowedDates(date) {
      return (
        (dayjs(date).isAfter(dayjs(), 'day') ||
          dayjs(date).isSame(dayjs(), 'day')) &&
        !this.isOccupied(date) &&
        this.usableStartDateFilter(date)
      )
    },
    allowedDatesOfStart(date) {
      return this.endDate
        ? // endDateが入力されている場合、すでに占有されている予算期間をまたぐことはできない
          (!this.occupiedPeriods.some(period => {
            const endDateInvalid =
              period.end !== null || period.end !== undefined
                ? dayjs(period.end).isBetween(
                    dayjs(date).startOf('day'),
                    dayjs(this.endDate).endOf('day'),
                    null,
                    '[]',
                  )
                : false

            const startDateInvalid =
              period.start !== null || period.start !== undefined
                ? dayjs(period.start).isBetween(
                    dayjs(date).startOf('day'),
                    dayjs(this.endDate).endOf('day'),
                    null,
                    '[]',
                  )
                : false

            return endDateInvalid || startDateInvalid
          }) &&
            this.allowedDates(date) &&
            this.usableStartDateFilter(date) &&
            dayjs(date).isBefore(this.endDate)) ||
            dayjs(date).isSame(this.endDate)
        : this.allowedDates(date)
    },
    allowedDatesOfEnd(date) {
      const base = this.startDate
        ? this.disabledStart
          ? dayjs()
              .startOf('day')
              .format(dateFormat)
          : this.startDate
        : null
      return base
        ? (dayjs(date).isAfter(base) || dayjs(date).isSame(base)) &&
            // 他の予算の期間を跨ぐことはできない
            !this.occupiedPeriods.some(period => {
              const endDateInvalid =
                period.end !== null || period.end !== undefined
                  ? dayjs(period.end).isBetween(
                      dayjs(date).startOf('day'),
                      dayjs(base).endOf('day'),
                      null,
                      '[]',
                    )
                  : false

              const startDateInvalid =
                period.start !== null || period.start !== undefined
                  ? dayjs(period.start).isBetween(
                      dayjs(date).startOf('day'),
                      dayjs(base).endOf('day'),
                      null,
                      '[]',
                    )
                  : false

              return endDateInvalid || startDateInvalid
            }) &&
            this.allowedDates(date) &&
            this.usableEndDateFilter(date, base)
        : this.allowedDates(date)
    },
    formatDate(date) {
      return dayjs(date).format('D')
    },
    format(date) {
      if (date == null) return null
      return dayjs(date).format(dateFormat)
    },
    isOccupied(date) {
      return this.occupiedPeriods.some(period => {
        if (period.end === undefined)
          return dayjs(date).isSame(period.start, 'day')
        return dayjs(date).isBetween(period.start, period.end, 'day', '[]')
      })
        ? ['#CCC']
        : false
    },
    validate() {
      this.errors.start = ''
      this.errors.end = ''
      if (this.required) {
        if (!this.start) {
          this.errors.start = '必須項目です'
        }
        if (!this.end) {
          this.errors.end = '必須項目です'
        }
      }
      if (this.start && this.end) {
        if (dayjs(this.start).isAfter(this.end)) {
          this.errors.start = '開始日は終了日より前の日付を選択してください'
          this.errors.end = '終了日は開始日より後の日付を選択してください'
        }
      }
      return this.errors.start === '' && this.errors.end === ''
    },
  },
}
</script>

<style scoped lang="scss">
.container {
  display: flex;
  padding: 0;
}
.date-picker-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.dash {
  margin-top: 10px;
}
.row {
  display: flex;
  align-items: center;
}

.button-delete {
  cursor: pointer;
  margin-left: -20px;
}
</style>
